import Link from 'next/link';
import { useTheme } from '@mui/material/styles';
import emojiFlags from 'emoji-flags';
import { slugFromCC } from 'tf-post-parser';
import { getLocationDetails } from '../../../helpers/getLocationDetails';
import staticMapUrl from '../../../helpers/staticMapUrl';
import { fraunces } from '../../Landing/fonts';
import FutureImage from '../../FutureImage';
import MapboxCredits from '../../Maps/MapboxCredits';

// "The destination" — static map, travel guide and map links for the
// post's location. Brings the old sidebar's most useful cards into the
// reading flow. On very wide logged-out screens the ConversionRail shows
// the map instead — `hideOnRail` hides this in-flow card there.
export default function DestinationCard({ location, hideOnRail }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  const mapImage = staticMapUrl(location, dark);
  if (!mapImage) return null;

  const { locationTitle, locationSubtitle, country } = getLocationDetails(
    location,
  );
  const [longitude, latitude] = location.coordinates.coordinates;
  const flagData = emojiFlags.countryCode(location.countryCode.toUpperCase());
  const flag = flagData ? flagData.emoji : null;
  const slug = slugFromCC(location.countryCode);

  return (
    <section
      className={`${
        hideOnRail ? 'tf-hide-on-rail-map ' : ''
      }tw-mx-auto tw-my-10 tw-max-w-[46rem] tw-px-4`}
    >
      <div className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)]">
        <div className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-2 tw-px-6 tw-pb-4 tw-pt-5 sm:tw-px-7">
          <div>
            <p className="tw-m-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.25em] tw-text-[#8A958F]">
              The destination
            </p>
            <p
              className={`${fraunces.className} tw-mb-0 tw-mt-1 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9]`}
            >
              {flag && (
                <span className="emoji-flag" aria-hidden="true">
                  {flag}{' '}
                </span>
              )}
              {locationSubtitle || locationTitle}
            </p>
          </div>
          <Link
            href={`/map?zoom=6&latitude=${encodeURIComponent(
              latitude,
            )}&longitude=${encodeURIComponent(longitude)}`}
            prefetch={false}
            className="tw-font-mono tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-primary-700 dark:tw-text-primary-300 tw-no-underline hover:tw-underline"
          >
            Open map →
          </Link>
        </div>
        <Link
          href={`/map?zoom=6&latitude=${encodeURIComponent(
            latitude,
          )}&longitude=${encodeURIComponent(longitude)}`}
          prefetch={false}
          className="tw-block"
        >
          <FutureImage
            src={mapImage}
            width={1344}
            height={600}
            alt={`Map of ${locationSubtitle || locationTitle}`}
            className="tw-h-auto tw-w-full tw-mix-blend-multiply dark:tw-mix-blend-screen"
          />
        </Link>
        <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2 tw-px-6 tw-pb-1 sm:tw-px-7">
          <span className="tw-pt-1.5 tw-font-mono tw-text-[10px] tw-font-medium tw-tracking-[0.22em] tw-text-[#8A958F]">
            <span aria-hidden="true">⌖ </span>
            {`${Math.abs(latitude).toFixed(4)}° ${
              latitude >= 0 ? 'N' : 'S'
            } · ${Math.abs(longitude).toFixed(4)}° ${
              longitude >= 0 ? 'E' : 'W'
            }`}
          </span>
          <MapboxCredits />
        </div>
        <div className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2 tw-px-6 tw-pb-5 tw-pt-1 sm:tw-px-7">
          <Link
            href={`/destinations/${slug}`}
            prefetch={false}
            className="tw-text-sm tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300 tw-no-underline hover:tw-underline"
          >
            {country} travel guide →
          </Link>
          <Link
            href={`/destinations/${slug}`}
            prefetch={false}
            className="tw-text-sm tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300 tw-no-underline hover:tw-underline"
          >
            More stories from {country} →
          </Link>
        </div>
      </div>
    </section>
  );
}
